import { useState } from "react";
import { Link } from "react-router-dom";
import { mockFactoryUser, mockFactoryStats, mockFactoryOrders } from "@/mocks/factory";
import ResellerCommissionPanel from "./components/ResellerCommissionPanel";

const statusConfig: Record<string, { label: string; color: string; icon: string }> = {
  processing: { label: "Processing", color: "bg-amber-100 text-amber-700", icon: "ri-loader-2-line" },
  shipped: { label: "Shipped", color: "bg-blue-100 text-blue-700", icon: "ri-truck-line" },
  delivered: { label: "Delivered", color: "bg-green-100 text-green-700", icon: "ri-checkbox-circle-line" },
  cancelled: { label: "Cancelled", color: "bg-red-100 text-red-700", icon: "ri-close-circle-line" },
};

const FactoryDashboard = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const recentOrders = mockFactoryOrders.slice(0, 5);

  return (
    <div className="min-h-screen bg-[#f8f7f5] flex" style={{ fontFamily: "'Inter', sans-serif" }}>
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 w-64 bg-[#1a0a0a] flex flex-col transition-transform duration-300 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        } lg:translate-x-0 lg:static lg:flex`}
      >
        {/* Logo */}
        <div className="p-5 border-b border-white/10">
          <Link to="/" className="flex items-center gap-3">
            <img
              src="https://static.readdy.ai/image/3fc89005e5f8f541d0781d07714068dd/b31ed3570265bb91107b593ba40da4ad.jpeg"
              alt="Crown Prime Mall"
              className="h-9 w-9 rounded-full object-cover"
            />
            <div>
              <div className="text-white font-bold text-sm">Crown Prime Mall</div>
              <div className="text-[#D4AF37] text-[10px]">Supplier Portal</div>
            </div>
          </Link>
        </div>

        {/* User Info */}
        <div className="p-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#C41E3A] flex items-center justify-center text-white font-bold text-sm">
              {mockFactoryUser.companyName.charAt(0)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-white text-xs font-semibold truncate">{mockFactoryUser.companyName}</div>
              <div className="flex items-center gap-1 mt-0.5">
                <span className="w-1.5 h-1.5 rounded-full bg-green-400 inline-block"></span>
                <span className="text-green-400 text-[10px]">Verified Supplier</span>
              </div>
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 p-4 space-y-1">
          {[
            { icon: "ri-dashboard-line", label: "Dashboard", to: "/factory/dashboard", active: true },
            { icon: "ri-box-3-line", label: "My Products", to: "/factory/products", active: false },
            { icon: "ri-shopping-bag-3-line", label: "Orders", to: "/factory/orders", active: false },
            { icon: "ri-bar-chart-2-line", label: "Analytics", to: "/factory/dashboard", active: false },
            { icon: "ri-message-3-line", label: "Messages", to: "/factory/dashboard", active: false },
            { icon: "ri-settings-3-line", label: "Settings", to: "/factory/dashboard", active: false },
          ].map((item) => (
            <Link
              key={item.label}
              to={item.to}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors cursor-pointer ${
                item.active
                  ? "bg-[#C41E3A] text-white"
                  : "text-gray-400 hover:bg-white/10 hover:text-white"
              }`}
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className={item.icon}></i>
              </div>
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="p-4 border-t border-white/10">
          <Link
            to="/factory/login"
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-gray-400 hover:bg-white/10 hover:text-white transition-colors cursor-pointer"
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-logout-box-line"></i>
            </div>
            Sign Out
          </Link>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Bar */}
        <header className="bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between sticky top-0 z-20">
          <div className="flex items-center gap-4">
            <button
              className="lg:hidden w-8 h-8 flex items-center justify-center text-gray-600 cursor-pointer"
              onClick={() => setSidebarOpen(true)}
            >
              <i className="ri-menu-line text-xl"></i>
            </button>
            <div>
              <h1 className="text-lg font-bold text-gray-900">Dashboard</h1>
              <p className="text-xs text-gray-400">Welcome back, {mockFactoryUser.companyName}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/factory/products"
              className="flex items-center gap-2 bg-[#C41E3A] text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-[#a01830] transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line"></i>
              <span className="hidden sm:inline">Add Product</span>
            </Link>
            <button className="relative w-9 h-9 flex items-center justify-center text-gray-500 hover:text-gray-700 cursor-pointer">
              <i className="ri-notification-3-line text-xl"></i>
              <span className="absolute top-1 right-1 w-2 h-2 bg-[#C41E3A] rounded-full"></span>
            </button>
          </div>
        </header>

        <main className="flex-1 p-6 space-y-6">
          {/* Stats Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              {
                icon: "ri-box-3-line",
                label: "Total Products",
                value: mockFactoryStats.totalProducts,
                sub: `${mockFactoryStats.activeListings} active`,
                color: "text-[#C41E3A]",
                bg: "bg-[#C41E3A]/10",
              },
              {
                icon: "ri-shopping-bag-3-line",
                label: "Total Orders",
                value: mockFactoryStats.totalOrders,
                sub: `${mockFactoryStats.pendingOrders} pending`,
                color: "text-amber-600",
                bg: "bg-amber-50",
              },
              {
                icon: "ri-money-dollar-circle-line",
                label: "Monthly Revenue",
                value: `$${mockFactoryStats.monthlyRevenue.toLocaleString()}`,
                sub: "USD this month",
                color: "text-green-600",
                bg: "bg-green-50",
              },
              {
                icon: "ri-star-line",
                label: "Supplier Rating",
                value: mockFactoryStats.rating,
                sub: `${mockFactoryStats.reviewCount} reviews`,
                color: "text-[#D4AF37]",
                bg: "bg-[#D4AF37]/10",
              },
            ].map((stat) => (
              <div key={stat.label} className="bg-white rounded-xl p-5 border border-gray-100">
                <div className={`w-10 h-10 flex items-center justify-center rounded-lg ${stat.bg} mb-3`}>
                  <i className={`${stat.icon} ${stat.color} text-xl`}></i>
                </div>
                <div className="text-2xl font-bold text-gray-900">{stat.value}</div>
                <div className="text-xs text-gray-500 mt-0.5">{stat.label}</div>
                <div className={`text-xs font-medium mt-1 ${stat.color}`}>{stat.sub}</div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Recent Orders */}
            <div className="lg:col-span-2 bg-white rounded-xl border border-gray-100 overflow-hidden">
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                <h2 className="font-bold text-gray-900 text-sm">Recent Orders</h2>
                <Link to="/factory/orders" className="text-xs text-[#C41E3A] hover:underline cursor-pointer whitespace-nowrap">
                  View all <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              <div className="divide-y divide-gray-50">
                {recentOrders.map((order) => {
                  const s = statusConfig[order.status];
                  return (
                    <div key={order.id} className="px-5 py-4 flex items-center gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="text-xs font-bold text-gray-900">{order.id}</span>
                          <span className={`flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full ${s.color}`}>
                            <i className={s.icon}></i> {s.label}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 truncate">{order.product}</div>
                        <div className="text-[10px] text-gray-400 mt-0.5">{order.buyer} &bull; {order.date}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-bold text-gray-900">${order.total.toLocaleString()}</div>
                        <div className="text-[10px] text-gray-400">Qty: {order.qty}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Quick Actions + Info */}
            <div className="space-y-4">
              {/* Quick Actions */}
              <div className="bg-white rounded-xl border border-gray-100 p-5">
                <h2 className="font-bold text-gray-900 text-sm mb-4">Quick Actions</h2>
                <div className="space-y-2">
                  {[
                    { icon: "ri-add-box-line", label: "Upload New Product", to: "/factory/products", color: "text-[#C41E3A]" },
                    { icon: "ri-shopping-bag-3-line", label: "View All Orders", to: "/factory/orders", color: "text-amber-600" },
                    { icon: "ri-store-2-line", label: "Browse Marketplace", to: "/products", color: "text-green-600" },
                    { icon: "ri-customer-service-2-line", label: "Contact Support", to: "/", color: "text-gray-600" },
                  ].map((action) => (
                    <Link
                      key={action.label}
                      to={action.to}
                      className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer group"
                    >
                      <div className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded-lg group-hover:bg-gray-200 transition-colors">
                        <i className={`${action.icon} ${action.color} text-sm`}></i>
                      </div>
                      <span className="text-sm text-gray-700 font-medium">{action.label}</span>
                      <div className="ml-auto w-4 h-4 flex items-center justify-center">
                        <i className="ri-arrow-right-s-line text-gray-400 text-sm"></i>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>

              {/* Supplier Badge */}
              <div className="bg-gradient-to-br from-[#1a0a0a] to-[#C41E3A] rounded-xl p-5 text-white">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-[#D4AF37] rounded-full">
                    <i className="ri-shield-check-fill text-[#1a0a0a] text-sm"></i>
                  </div>
                  <span className="font-bold text-sm">Verified Supplier</span>
                </div>
                <p className="text-white/70 text-xs leading-relaxed mb-3">
                  Your account is verified. Buyers trust your listings with the Crown Fortune badge.
                </p>
                <div className="flex items-center gap-1">
                  {[1,2,3,4,5].map((s) => (
                    <div key={s} className="w-4 h-4 flex items-center justify-center">
                      <i className={`ri-star-fill text-xs ${s <= 4 ? "text-[#D4AF37]" : "text-white/30"}`}></i>
                    </div>
                  ))}
                  <span className="text-xs text-white/70 ml-1">{mockFactoryStats.rating} / 5.0</span>
                </div>
              </div>
            </div>
          </div>
          {/* Reseller Commission Settings */}
          <ResellerCommissionPanel />
        </main>
      </div>
    </div>
  );
};

export default FactoryDashboard;
